import { createSlice } from '@reduxjs/toolkit';
import { initialChatbotNewState } from './chatbotNewConstants';
import { fetchAndInitChatbotNew } from './chatbotNewThunks';
import { initChatbotNewFromApiResponse } from './chatbotNewUtils';
import CONSTANT from '../../constants/Constant';
import GLOBAL from '../../global';

const chatbotNewSlice = createSlice({
    name: 'chatBotNew',
    initialState: initialChatbotNewState,
    reducers: {
        setCurrentQuestionIndex(state, action) {
            state.currentQuestionIndex = action.payload;
        },
        advanceToNextQuestion(state) {
            const current = state.flatQuestions[state.currentQuestionIndex];
            if (current) {
                // Caution Check (Biometric Group) if user answered "Yes"
                if (current.groupId === CONSTANT.ASSESSMENT_GROUP.BIOMETRIC.key) {
                    const selectedResponse = (current.responses || []).find(r => Number(r.id) == Number(current.answerId));
                    if (selectedResponse && (selectedResponse.name || '').toLowerCase() === 'yes') {
                        state.showBiometricCautionAlert = true;
                        return; // Stop advancing, show alert first
                    }
                }

                const alreadyIn = state.answeredQuestions.some(q => q.questionId === current.questionId);
                if (!alreadyIn) {
                    state.answeredQuestions.push({ ...current });
                }
            }

            let nextIndex = state.currentQuestionIndex + 1;
            while (nextIndex < state.flatQuestions.length && state.flatQuestions[nextIndex].isDefaultHide) {
                nextIndex++;
            }

            if (nextIndex >= state.flatQuestions.length) {
                // All questions answered - check invitation type for biometric handling
                state.phase = 'completed';

                const invitationType = (state.invitationTypeName || '').toLowerCase();
                const { isExamineticsEnabled } = state.tenantFeatures || {};
                const isSelfAssessment = invitationType !== CONSTANT.REDIRECT_TYPE.INCLINIC && invitationType !== CONSTANT.REDIRECT_TYPE.TELEMEDICINE;

                if (invitationType === CONSTANT.REDIRECT_TYPE.INCLINIC) {
                    // In-clinic: Skip biometric, mark completed
                    state.biometricStatus = 'skipped';
                    state.showBiometricPrompt = true; // Will show OK-only prompt
                } else if (invitationType === CONSTANT.REDIRECT_TYPE.TELEMEDICINE) {
                    // Telemedicine: Show biometric prompt with Join options
                    state.biometricStatus = 'pending';
                    state.showBiometricPrompt = true;
                } else {
                    // Self-assessment: Show biometric prompt
                    state.biometricStatus = 'pending';
                    state.showBiometricPrompt = true;

                    // For Examinetics Self Assessment, go directly to biometric modal
                    if (isExamineticsEnabled && isSelfAssessment) {
                        state.biometricModalOpen = true;
                    }
                }
            } else {
                state.currentQuestionIndex = nextIndex;
            }
        },
        setQuestionAnswerInNew(state, action) {
            const { questionId, answerId, answerValue, toggleCheckbox } = action.payload;
            const question = state.flatQuestions.find(q => q.questionId === questionId);
            if (!question) return;

            if (toggleCheckbox) {
                const tempList = question.answerList ?? [];
                const alreadySelected = tempList.includes(answerId);
                question.answerList = alreadySelected
                    ? tempList.filter(id => id !== answerId)
                    : [...tempList, answerId];
            } else {
                question.answerId = answerId ?? 0;
                question.answerValue = answerValue ?? '';
            }
        },
        clearQuestionAnswerInNew(state, action) {
            const { questionId } = action.payload;
            const question = state.flatQuestions.find(q => q.questionId === questionId);
            if (!question) return;
            question.answerId = 0;
            question.answerValue = '';
            question.answerList = [];
        },
        handleHideOrShowQuestionInNew(state, action) {
            const { questionId, isDefaultHide } = action.payload;
            const question = state.flatQuestions.find(q => q.questionId === questionId);
            if (!question) return;
            question.isDefaultHide = isDefaultHide;
            if (isDefaultHide) {
                question.answerId = 0;
                question.answerValue = '';
                question.answerList = [];
            }
        },
        setEditingQuestionIndex(state, action) {
            const editIndex = action.payload;
            state.editingQuestionIndex = editIndex;

            if (editIndex >= 0 && editIndex < state.flatQuestions.length) {
                const editQuestionId = state.flatQuestions[editIndex]?.questionId;
                const editPosInAnswered = state.answeredQuestions.findIndex(
                    q => q.questionId === editQuestionId
                );
                if (editPosInAnswered >= 0) {
                    state.answeredQuestions = state.answeredQuestions.slice(0, editPosInAnswered);
                }
            }
        },
        setIsSaving(state, action) {
            state.isSaving = action.payload;
        },
        setChatbotNewError(state, action) {
            state.error = action.payload;
            state.phase = 'error';
        },
        clearChatbotNewState() {
            return initialChatbotNewState;
        },
        setBiometricModalOpen(state, action) {
            state.biometricModalOpen = action.payload;
        },
        setShowBiometricPrompt(state, action) {
            state.showBiometricPrompt = action.payload;
        },
        setChatbotNewPhase(state, action) {
            state.phase = action.payload;
        },
        setBlocked(state, action) {
            state.isBlocked = true;
            state.exitWorkflowMessage = action.payload;
            state.phase = 'blocked';
        },
        setShowBiometricCautionAlert(state, action) {
            state.showBiometricCautionAlert = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAndInitChatbotNew.pending, (state) => {
                state.phase = 'loading';
            })
            .addCase(fetchAndInitChatbotNew.fulfilled, (state, action) => {
                initChatbotNewFromApiResponse(state, action.payload);
            })
            .addCase(fetchAndInitChatbotNew.rejected, (state, action) => {
                state.phase = 'error';
                state.error = action.payload || 'Failed to load assessment data';
            });
    },
});

export const {
    setCurrentQuestionIndex,
    advanceToNextQuestion,
    setQuestionAnswerInNew,
    handleHideOrShowQuestionInNew,
    setEditingQuestionIndex,
    setIsSaving,
    setChatbotNewError,
    clearChatbotNewState,
    setBiometricModalOpen,
    setShowBiometricPrompt,
    setChatbotNewPhase,
    setBlocked,
    setShowBiometricCautionAlert,
    clearQuestionAnswerInNew,
} = chatbotNewSlice.actions;

export default chatbotNewSlice.reducer;
